import React, { useMemo } from 'react'
import { UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateType } from '@node-space/storybook-components/dist'
import { Box } from '@node-space/storybook-components/dist/Box'
import { DateErrorType } from '@node-space/storybook-components/dist/DateOfBirth'
import { useDateInput } from 'hooks/forms/useDateInput'
import { AddCustomerFormErrors, AddCustomerPayload } from 'types/customers'
import { CustomerAddress } from '../CustomerAddress'
import { Combobox, ComboboxItem, Input, SingleDate } from '@node-space/ui-components'

interface IndividualCustomerFieldsProps {
  errors?: AddCustomerFormErrors
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  isLocalBeneficiary?: boolean
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
  countryCode?: string
  countries?: ComboboxItem[]
  isCountriesLoading?: boolean
  personalIdNumber: string
  individualCustomerDescription?: string
  register?: UseFormRegister<AddCustomerPayload>
  setValue?: (name: string, value: string) => void
  renderCurrencySpecificFields?: () => React.ReactNode
}

export const IndividualCustomerFields = ({
  errors,
  firstName,
  lastName,
  dateOfBirth,
  addressLine1,
  addressLine2,
  city,
  postalCode,
  countryCode,
  countries,
  isCountriesLoading,
  personalIdNumber,
  individualCustomerDescription,
  register,
  setValue,
}: IndividualCustomerFieldsProps) => {
  const { t } = useTranslation()

  const {
    dateLabels,
    hasDateErrors,
    getDateFields,
    setDateInputValue,
    setDateErrors,
    getDateErrors,
    getDateErrorText,
  } = useDateInput()

  const dateOfBirthFieldLables = useMemo(() => {
    return {
      ...dateLabels,
      dateOfBirth: t('formFields.dateOfBirth'),
      date: t('date'),
      day: t('day'),
      month: t('month'),
      year: t('year'),
    }
  }, [errors?.dateOfBirth])

  const handleonDateOfBirthChange = (date: DateType) => {
    setValue('dateOfBirth', setDateInputValue(date))
  }

  const handleNationalitySelect = (countryCode: string) => {
    const nationalityName = countries.find(country => country?.value === countryCode)?.leftText as string
    setValue('nationality', countryCode)
    setValue('nationalityName', nationalityName)
  }

  const onChange = (field: string, value: string) => {
    setValue(field, value)
  }

  return (
    <Box flex direction="col" gapY={16}>
      <Box flex direction="row" gapX={16}>
        <Box flexSize="auto" width="full">
          <Input
            {...register('firstName')}
            label={t('formFields.firstName')}
            placeholder={t('enterName')}
            inputMode="text"
            name="firstName"
            testId="first-name"
            errorText={errors?.firstName?.message}
            error={!!errors?.firstName}
            value={firstName}
            onChange={e => setValue('firstName', e?.target?.value)}
          />
        </Box>
        <Box flexSize="auto" width="full">
          <Input
            {...register('lastName')}
            label={t('formFields.lastName')}
            placeholder={t('enterLastName')}
            inputMode="text"
            name="lastName"
            testId="last-name"
            errorText={errors?.lastName?.message}
            error={!!errors?.lastName}
            value={lastName}
            onChange={e => setValue('lastName', e?.target?.value)}
          />
        </Box>
      </Box>

      <Input
        {...register('personalIdNumber')}
        label={t('customers.personalIdNumber')}
        inputMode="text"
        name="personalIdNumber"
        testId="personalIdNumber"
        errorText={errors?.personalIdNumber?.message}
        error={!!errors?.personalIdNumber}
        value={personalIdNumber}
        onChange={e => setValue('personalIdNumber', e?.target?.value)}
      />

      <SingleDate
        {...register('dateOfBirth')}
        date={getDateFields(dateOfBirth)}
        labels={{
          ...dateOfBirthFieldLables,
          errorMessage: getDateErrorText(errors?.dateOfBirth),
        }}
        errors={getDateErrors(errors?.dateOfBirth)}
        onChange={handleonDateOfBirthChange}
        onValidate={(errors: DateErrorType) => setDateErrors(errors)}
      />

      <Box flexSize="fill">
        <Combobox
          {...register('nationality')}
          items={countries}
          loading={isCountriesLoading}
          label={t('customers.countryOfNationality')}
          searchPlaceholder={t('searchOrSelectACountry')}
          selectPlaceholder={t('searchOrSelectACountry')}
          onChange={handleNationalitySelect}
          id="nationality"
          errorText={errors?.nationality?.message}
          defaultValue={countryCode}
        />
      </Box>

      <Input
        {...register('individualCustomerDescription')}
        label={t('customers.internalCustomerDescription')}
        inputMode="text"
        name="individual-customer-description"
        testId="individual-customer-description"
        errorText={errors?.individualCustomerDescription?.message}
        error={!!errors?.individualCustomerDescription}
        value={individualCustomerDescription}
        onChange={(e) => onChange('individualCustomerDescription', e?.target?.value)}
      />

      <CustomerAddress
        title={t('customers.residentialAddress')}
        errors={errors}
        addressLine1={addressLine1}
        addressLine2={addressLine2}
        city={city}
        postalCode={postalCode}
        countryCode={countryCode}
        countries={countries}
        isCountriesLoading={isCountriesLoading}
        register={register}
        setValue={setValue}
      />
    </Box>
  )
}
